import type { Node } from '@xyflow/react'
import { z } from 'zod'
import {
  agentSettingsSchema,
  conversationGoalSchema,
} from '../config/agent-settings.validation.config'

export const positionSchema = z.object({ x: z.number(), y: z.number() })

export const basicNodeSchema = z.object({
  id: z.string(),
  position: positionSchema.optional(),
  data: z.any(),
  type: z.string(),
  width: z.number().optional().nullable(),
  height: z.number().optional().nullable(),
  selected: z.boolean().optional().nullable(),
  positionAbsolute: positionSchema.optional().nullable(),
  dragging: z.boolean().optional().nullable(),
  selectable: z.boolean().optional().nullable(),
  connectable: z.boolean().optional().nullable(),
  deletable: z.boolean().optional().nullable(),
  focusable: z.boolean().optional().nullable(),
  dragHandle: z.string().optional().nullable(),
  className: z.string().optional().nullable(),
  style: z.record(z.any()).optional().nullable(),
  hidden: z.boolean().optional().nullable(),
  ariaLabel: z.string().optional().nullable(),
  zIndex: z.number().optional().nullable(),
  extent: z
    .union([z.literal('parent'), z.custom<[[number, number], [number, number]]>()])
    .optional()
    .nullable(),
  parentNode: z.string().optional().nullable(),
  expandParent: z.boolean().optional().nullable(),
  resizing: z.boolean().optional().nullable(),
})

export const basicEdgeSchema = z.object({
  id: z.string(),
  source: z.string(),
  target: z.string(),
  type: z.string().optional(),
  label: z.string().optional(),
  data: z.any().optional(),
  sourceHandle: z.string().optional().nullable(),
  targetHandle: z.string().optional().nullable(),
  selected: z.boolean().optional().nullable(),
  animated: z.boolean().optional().nullable(),
  markerStart: z.any().optional().nullable(),
  markerEnd: z.any().optional().nullable(),
  style: z.record(z.any()).optional().nullable(),
  className: z.string().optional().nullable(),
  sourceNode: z.custom<Node>().optional().nullable(),
  targetNode: z.custom<Node>().optional().nullable(),
  interactionWidth: z.number().optional().nullable(),
  hidden: z.boolean().optional().nullable(),
  deletable: z.boolean().optional().nullable(),
  focusable: z.boolean().optional().nullable(),
  zIndex: z.number().optional().nullable(),
  ariaLabel: z.string().optional().nullable(),
})

// Schema for system prompt
export const systemPromptSchema = z.object({
  prompt: z.string(),
  ai_nodes: z.array(z.string()),
  all_nodes_enabled: z.boolean().optional(),
})

// This is used to validate if the imported or pasted flow is valid.
export const reactFlowContentSchema = z.object({
  name: z.string().nullable().optional(),
  flowId: z.string().nullable().optional(),
  nodes: z.array(basicNodeSchema),
  edges: z.array(basicEdgeSchema).optional().default([]),
  systemPrompt: systemPromptSchema.nullable().optional(),
  sttGuidance: z.string().nullable().optional(),
  agentName: z.string().nullable().optional(),
  agentPersona: z.string().nullable().optional(),
  conversationGoals: z.array(conversationGoalSchema).nullable().optional(),
  agentSettings: agentSettingsSchema.nullable().optional(),
})
